import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import GestureIcon from '@mui/icons-material/Gesture';
import type { MediaDocType } from '@ast-data/shared';
import { useMediaThumbUrl } from './useMediaBlobUrl';

/**
 * The pieces a plate is drawn from on screen, at the proportions the PDF
 * prints it in (see plateGeometry): a picture at its cell's size, and the
 * line under it.
 */

/**
 * One picture at the size its row gives it — its preview, marked as a sketch
 * where it is one. Reports the preview's real shape once it has loaded.
 */
export function PlateImage({
    media,
    width,
    height,
    onAspect,
    children
}: {
    media: MediaDocType;
    width: number;
    height: number;
    onAspect: (id: string, aspect: number) => void;
    /** Laid over the picture's corner — a remove button, a drag grip. */
    children?: ReactNode;
}) {
    const { url, loading } = useMediaThumbUrl(media.id, media.updatedAt);
    return (
        <Box
            sx={{
                position: 'relative',
                width,
                height,
                flexShrink: 0,
                bgcolor: 'action.hover',
                overflow: 'hidden'
            }}
        >
            {url ? (
                <Box
                    component="img"
                    src={url}
                    alt={media.caption || media.originalName}
                    draggable={false}
                    onLoad={(event) => {
                        const image = event.currentTarget;
                        if (image.naturalWidth && image.naturalHeight) {
                            onAspect(
                                media.id,
                                image.naturalWidth / image.naturalHeight
                            );
                        }
                    }}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        display: 'block'
                    }}
                />
            ) : (
                <Box
                    sx={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    {loading ? (
                        <CircularProgress size={20} />
                    ) : (
                        <BrokenImageIcon color="disabled" />
                    )}
                </Box>
            )}
            {media.kind === 'sketch' && (
                <GestureIcon
                    fontSize="small"
                    sx={{
                        position: 'absolute',
                        top: 4,
                        left: 4,
                        color: 'common.white',
                        filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.6))'
                    }}
                />
            )}
            {children}
        </Box>
    );
}

/** The line under a picture: the name the PDF gives it, and its own. */
export function PlateCaption({
    width,
    label,
    wrap = false
}: {
    width: number;
    label: ReactNode;
    /** At full length, as the plate prints it; else cut to one line. */
    wrap?: boolean;
}) {
    return (
        <Typography
            variant="caption"
            component="div"
            noWrap={!wrap}
            sx={{ width, color: 'text.secondary' }}
        >
            {label}
        </Typography>
    );
}
